import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import stripePromise from "../config/stripeConfig";
import { createSubscription } from "../services/paymentService";
import { getOrganizationDetails } from "../services/organizationService";
import CountrySelect from "./CountrySelect";

const PRICE_PER_ROOM = 5;

// Stripe CardElement configuration - hide postal code since we collect full billing address separately
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
  hidePostalCode: true, // We collect postal code in the billing address form above
};

function SubscriptionSetupForm({ organizationId, onSuccess }) {
  const { user } = useSelector((state) => state.user);
  
  const [loading, setLoading] = useState(false);
  const [billingEmail, setBillingEmail] = useState(() => {
    // Initialize with user's email if available
    return user?.email || "";
  });
  const [error, setError] = useState("");
  const [maxRooms, setMaxRooms] = useState(1); // Default to 1 room
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });
  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState(true);
  const [organization, setOrganization] = useState(null);
  const hasSetDefaultEmail = useRef(false);

  const stripe = useStripe();
  const elements = useElements();
  const { enqueueSnackbar } = useSnackbar();

  // Load organization data
  useEffect(() => {
    const loadOrganizationData = async () => {
      try {
        const orgData = await getOrganizationDetails();
        setOrganization(orgData);
        
        // If organization has company address and sameAsCompanyAddress is true, copy it
        if (orgData?.companyAddress && sameAsCompanyAddress) {
          setBillingAddress(orgData.companyAddress);
        }
      } catch (error) {
        console.error('Error loading organization data:', error);
      }
    };

    loadOrganizationData();
  }, []);

  // Set default billing email to user's email if it wasn't set during initialization
  useEffect(() => {
    if (user && user.email && !billingEmail && !hasSetDefaultEmail.current) {
      setBillingEmail(user.email);
      hasSetDefaultEmail.current = true;
    }
  }, [user]); // Only depend on user, not billingEmail to avoid infinite loop

  // Handle "same as company address" checkbox
  useEffect(() => {
    if (sameAsCompanyAddress && organization?.companyAddress) {
      setBillingAddress(organization.companyAddress);
    } else if (!sameAsCompanyAddress) {
      setBillingAddress({
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
      });
    }
  }, [sameAsCompanyAddress, organization]);

  const handleBillingAddressChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!maxRooms || maxRooms < 1) {
      setError("Please enter a valid number of rooms (minimum 1)");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: billingEmail,
          address: {
            line1: billingAddress.line1,
            line2: billingAddress.line2,
            city: billingAddress.city,
            state: billingAddress.state,
            postal_code: billingAddress.postal_code,
            country: billingAddress.country,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Create subscription
      const response = await createSubscription({
        organizationId,
        paymentMethodId: paymentMethod.id,
        billingEmail,
        maxRooms: maxRooms,
        billingAddress: billingAddress,
      });

      // Handle payment confirmation if needed
      if (response.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(response.clientSecret);
        
        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }
      }

      enqueueSnackbar("Subscription created successfully!", { variant: "success" });
      onSuccess(response.subscription);

    } catch (err) {
      console.error("Subscription creation error:", err);
      const errorMessage = err.response?.data?.error || err.message || "Failed to create subscription";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Set Up Your Subscription
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Room Selection */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          How Many Rooms Do You Need?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose the number of meeting rooms for your organization. You can change this anytime.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <TextField
            label="Number of Rooms"
            type="number"
            fullWidth
            required
            value={maxRooms}
            onChange={(e) => setMaxRooms(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: 1000 }}
            sx={{ mb: 3 }}
            disabled={loading}
            helperText="Minimum 1 room, maximum 1000 rooms"
          />

          {/* Pricing Display */}
          <Card variant="outlined" sx={{ p: 3, bgcolor: 'primary.50' }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" gutterBottom>
                ${maxRooms * PRICE_PER_ROOM}
                <Typography variant="body2" component="span" color="text.secondary">
                  /month
                </Typography>
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                {maxRooms} Room{maxRooms !== 1 ? 's' : ''}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                ${PRICE_PER_ROOM} per room per month
              </Typography>
            </Box>
          </Card>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Payment Information */}
        <Typography variant="h6" gutterBottom>
          Payment Information
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Billing Email"
            type="email"
            fullWidth
            required
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            sx={{ mb: 3 }}
            disabled={loading}
            helperText="Billing notifications will be sent to this email address"
          />

          {/* Billing Address Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Billing Address
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsCompanyAddress}
                onChange={(e) => setSameAsCompanyAddress(e.target.checked)}
                disabled={loading}
              />
            }
            label="Same as company address"
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                name="line1"
                value={billingAddress.line1}
                onChange={handleBillingAddressChange}
                required
                placeholder="Street address"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                name="line2"
                value={billingAddress.line2}
                onChange={handleBillingAddressChange}
                placeholder="Apartment, suite, etc."
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={billingAddress.city}
                onChange={handleBillingAddressChange}
                required
                placeholder="City"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="State/Province"
                name="state"
                value={billingAddress.state}
                onChange={handleBillingAddressChange}
                placeholder="State"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                value={billingAddress.postal_code}
                onChange={handleBillingAddressChange}
                placeholder="ZIP/Postal code"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <CountrySelect
                value={billingAddress.country}
                onChange={(value) => setBillingAddress(prev => ({ ...prev, country: value }))}
                required
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              💡 <strong>Note:</strong> Make sure the postal code matches your card's billing address for successful payment verification.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Payment Method Section */}
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your card details below. We'll use the billing address you entered above for verification.
          </Typography>
          
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <CardElement options={cardElementOptions} />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test Cards:
            <br />
            • Success: 4242 4242 4242 4242
            <br />
            • Decline: 4000 0000 0000 0002
            <br />
            Use any future date and any 3-digit CVC.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!stripe || loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating Subscription...
              </>
            ) : (
              "Create Subscription"
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SubscriptionSetup({ organizationId, onSuccess }) {
  return (
    <Elements stripe={stripePromise}>
      <SubscriptionSetupForm organizationId={organizationId} onSuccess={onSuccess} />
    </Elements>
  );
} 